import { useMemo } from 'react';
import { Check, CheckCircle2, Loader2, RotateCcw, X, XCircle } from 'lucide-react';
import type { ChangeDecision, DiffStatus, ProposedChange, UIMessage } from '@/types/chat';
import { useChatStore } from '@/stores/chatStore';
import { applyDiffMessage, revertDiffMessage } from '@/services/applyFlow';
import { confirmChanges } from '@/services/chat';
import { extractErrorMessage } from '@/services/api';

interface Props {
  conversationId: string | null;
  message: UIMessage;
}

// DiffPreview renders the PRD §13.2 Screen 4 review dialog inline above
// an assistant message. User selects → Apply Selected → Office.js batch
// writes → confirm audit endpoint → store reflects applied state.
export function DiffPreview({ conversationId, message }: Props) {
  const { toggleDiffChange, setDiffAllSelected, setDiffStatus, setApplyProgress, stopAutoBuild, applyProgress } = useChatStore();
  const diff = message.diff!;
  const { status, changes, selected, dropped } = diff;

  const selectedCount = useMemo(
    () => changes.filter((c) => selected[c.id]).length,
    [changes, selected],
  );

  const onApply = async () => {
    // Apply as fast as Office.js allows (no artificial pacing) and show a
    // live progress label. The whole dashboard is built in one turn now, so
    // there is NO auto-continue — one Apply, done.
    await applyDiffMessage(conversationId, message, {
      onProgress: (done, total, label) => setApplyProgress({ messageId: message.id, done, total, label }),
    });
    setApplyProgress(null);
  };

  const onRejectAll = async () => {
    // A reject during an auto-build stops the chain — stopAutoBuild bumps a
    // nonce so ChatInterface also tears down its orchestrator refs.
    stopAutoBuild();
    if (!conversationId) {
      setDiffStatus(message.id, 'rejected', []);
      return;
    }
    const decisions: ChangeDecision[] = changes.map((c) => ({
      change_id: c.id,
      outcome: 'rejected',
    }));
    setDiffStatus(message.id, 'applying');
    try {
      await confirmChanges({
        conversation_id: conversationId,
        message_id: message.id,
        decisions,
      });
    } catch (err) {
      console.warn('confirmChanges failed:', extractErrorMessage(err));
    }
    setDiffStatus(message.id, 'rejected', []);
  };

  // Once resolved, render a terse summary and hide the action buttons.
  if (status !== 'streaming' && status !== 'pending' && status !== 'applying') {
    return <ResolvedSummary status={status} count={changes.length} message={message} />;
  }

  // During streaming, approval is disabled — the final set hasn't arrived
  // yet so the server may still merge/drop entries. The user sees each
  // change land live with a "proposing…" pulse.
  const isStreaming = status === 'streaming';
  const isApplying = status === 'applying';
  const actionsDisabled = isStreaming || isApplying;

  return (
    <div className="w-full max-w-[95%] rounded-lg border border-primary/30 bg-white shadow-card dark:bg-surface-dark dark:border-primary/40">
      <div className="flex items-center justify-between border-b border-border px-3 py-2 dark:border-border-dark">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          <span className="text-sm font-medium">
            {isStreaming
              ? `Proposing changes… (${changes.length})`
              : isApplying
                ? (applyProgress?.messageId === message.id
                    ? `Applying ${applyProgress.done} of ${applyProgress.total}… ${applyProgress.label}`
                    : 'Applying…')
                : `Review ${changes.length} change${changes.length === 1 ? '' : 's'}`}
          </span>
        </div>
        {isApplying || isStreaming ? (
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        ) : (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setDiffAllSelected(message.id, true)}
              className="btn-ghost text-[11px] px-2 py-1"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => setDiffAllSelected(message.id, false)}
              className="btn-ghost text-[11px] px-2 py-1"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      <ul className="divide-y divide-border dark:divide-border-dark max-h-72 overflow-y-auto">
        {changes.map((c) => (
          <ChangeRow
            key={c.id}
            change={c}
            selected={!!selected[c.id]}
            disabled={actionsDisabled}
            streaming={isStreaming}
            onToggle={() => toggleDiffChange(message.id, c.id)}
          />
        ))}
      </ul>

      {dropped && dropped.length > 0 && (
        <div className="border-t border-border px-3 py-2 text-xs text-text-secondary dark:border-border-dark">
          {dropped.length} change{dropped.length === 1 ? '' : 's'} invalid — dropped.
        </div>
      )}

      <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 dark:border-border-dark">
        <button
          type="button"
          onClick={onRejectAll}
          disabled={actionsDisabled}
          className="btn-ghost text-xs"
        >
          <X className="mr-1 h-3.5 w-3.5" /> Reject all
        </button>
        <button
          type="button"
          onClick={onApply}
          disabled={actionsDisabled || selectedCount === 0}
          className="btn-primary text-xs"
          title={isStreaming ? 'Waiting for the agent to finish proposing changes…' : undefined}
        >
          <Check className="mr-1 h-3.5 w-3.5" />
          {isStreaming
            ? 'Waiting…'
            : `Apply ${selectedCount > 0 ? `(${selectedCount})` : 'Selected'}`}
        </button>
      </div>
    </div>
  );
}

function ResolvedSummary({ status, count, message }: { status: DiffStatus; count: number; message: UIMessage }) {
  const label =
    status === 'applied'
      ? `${count} change${count === 1 ? '' : 's'} applied`
      : status === 'rejected'
      ? 'Changes rejected'
      : status === 'partial'
      ? `${count} change${count === 1 ? '' : 's'} applied (some failed)`
      : '';
  const Icon =
    status === 'applied'
      ? CheckCircle2
      : status === 'partial'
      ? CheckCircle2
      : XCircle;
  const tone =
    status === 'applied'
      ? 'text-green-600 border-green-200 bg-green-50 dark:border-green-900/60 dark:bg-green-900/20'
      : status === 'partial'
      ? 'text-amber-600 border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-900/20'
      : 'text-text-secondary border-border bg-surface dark:border-border-dark dark:bg-surface-dark';
  const canRevert = status === 'applied' || status === 'partial';
  return (
    <div className={['flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs', tone].join(' ')}>
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
      {canRevert && (
        <button
          type="button"
          onClick={() => void revertDiffMessage(message)}
          className="ml-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] opacity-80 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10"
          title="Undo these changes"
        >
          <RotateCcw className="h-3 w-3" />
          Revert
        </button>
      )}
    </div>
  );
}

function ChangeRow({
  change, selected, disabled, streaming, onToggle,
}: {
  change: ProposedChange;
  selected: boolean;
  disabled: boolean;
  streaming?: boolean;
  onToggle: () => void;
}) {
  const isSheetLevel =
    change.type === 'create_sheet' ||
    change.type === 'rename_sheet' ||
    change.type === 'delete_sheet';

  const isAdvanced =
    change.type === 'create_chart' ||
    change.type === 'create_pivot_table' ||
    change.type === 'delete_pivot_table' ||
    change.type === 'refresh_pivot_table' ||
    change.type === 'delete_chart' ||
    change.type === 'apply_conditional_format' ||
    change.type === 'sort_range' ||
    change.type === 'delete_row' ||
    change.type === 'delete_rows_where' ||
    change.type === 'delete_column' ||
    change.type === 'resize_column' ||
    change.type === 'resize_row' ||
    change.type === 'merge_cells' ||
    change.type === 'unmerge_cells' ||
    change.type === 'freeze_panes' ||
    change.type === 'find_replace' ||
    change.type === 'filter_range' ||
    change.type === 'clear_filter' ||
    change.type === 'transpose_range' ||
    change.type === 'text_to_columns' ||
    change.type === 'create_named_range' ||
    change.type === 'delete_named_range' ||
    change.type === 'apply_data_validation' ||
    change.type === 'clear_data_validation' ||
    change.type === 'protect_sheet' ||
    change.type === 'unprotect_sheet' ||
    change.type === 'hide_sheet' ||
    change.type === 'show_sheet' ||
    change.type === 'reorder_sheet' ||
    change.type === 'add_comment' ||
    change.type === 'delete_comment';

  // Sheet-level and advanced analytics changes share this compact layout:
  // a verb + context line + optional rationale. No before/after diff rows
  // because the "before" is "no object" and "after" is a structural
  // operation, not a cell value.
  if (isSheetLevel || isAdvanced) {
    return (
      <li
        className={[
          'flex items-start gap-2 px-3 py-2 text-xs',
          streaming ? 'bg-primary/5 animate-in fade-in slide-in-from-left-1' : '',
        ].join(' ')}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          disabled={disabled}
          className="mt-0.5 h-3.5 w-3.5 accent-primary"
        />
        <div className="min-w-0 flex-1">
          <div className="font-medium flex items-center gap-1.5">
            {structuralLabel(change)}
            {streaming && (
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            )}
          </div>
          <div className="mt-0.5 text-text-secondary">{structuralDetails(change)}</div>
          {change.reason && (
            <div className="mt-0.5 text-text-secondary italic">{change.reason}</div>
          )}
        </div>
      </li>
    );
  }

  const target = change.sheet + '!' + (change.cell ?? change.range ?? '');
  const before = formatSide(change.before_formula, change.before);
  const after = formatSide(change.after_formula, change.after) || diffAfterSummary(change);

  return (
    <li
      className={[
        'flex items-start gap-2 px-3 py-2 text-xs',
        streaming ? 'bg-primary/5 animate-in fade-in slide-in-from-left-1' : '',
      ].join(' ')}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        disabled={disabled}
        className="mt-0.5 h-3.5 w-3.5 accent-primary"
      />
      <div className="min-w-0 flex-1">
        <div className="font-mono font-medium flex items-center gap-1.5">
          {target}
          {streaming && (
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
          )}
        </div>
        <div className="mt-0.5 space-y-0.5 font-mono">
          {before && (
            <div className="text-red-600 dark:text-red-400">
              <span className="mr-1 select-none">-</span>
              <span className="break-all">{before}</span>
            </div>
          )}
          <div className="text-green-700 dark:text-green-400">
            <span className="mr-1 select-none">+</span>
            <span className="break-all">{after || '(cleared)'}</span>
          </div>
        </div>
        {change.reason && (
          <div className="mt-1 text-text-secondary italic">{change.reason}</div>
        )}
      </div>
    </li>
  );
}

// structuralLabel renders a short verb + object line. Used for sheet
// operations and advanced analytics where there's no before/after cell
// diff to show.
function structuralLabel(c: ProposedChange): string {
  switch (c.type) {
    case 'create_sheet':
      return `Create sheet "${c.sheet}"`;
    case 'rename_sheet':
      return `Rename sheet "${c.sheet}" → "${c.new_name ?? ''}"`;
    case 'delete_sheet':
      return `Delete sheet "${c.sheet}"`;
    case 'create_chart':
      return `Insert chart (${c.chart?.chart_type || 'chart'}) on "${c.sheet}"`;
    case 'create_pivot_table':
      return `Create pivot "${c.pivot?.name ?? ''}" on "${c.sheet}"`;
    case 'delete_pivot_table':
      return `Delete pivot "${c.pivot?.name ?? ''}"`;
    case 'refresh_pivot_table':
      return `Refresh pivot "${c.pivot?.name ?? ''}"`;
    case 'delete_chart':
      return `Delete chart "${c.chart?.title ?? ''}" on "${c.sheet}"`;
    case 'delete_row':
      return `Delete ${c.count ?? 1} row${(c.count ?? 1) === 1 ? '' : 's'} at ${c.sheet}!${c.cell ?? c.range ?? ''}`;
    case 'delete_rows_where':
      return `Delete matching rows in ${c.sheet}!${c.range ?? ''}`;
    case 'delete_column':
      return `Delete ${c.count ?? 1} column${(c.count ?? 1) === 1 ? '' : 's'} at ${c.sheet}!${c.cell ?? c.range ?? ''}`;
    case 'resize_column':
      return `Resize column ${c.cell ?? c.range ?? ''} on "${c.sheet}"`;
    case 'resize_row':
      return `Resize row ${c.cell ?? c.range ?? ''} on "${c.sheet}"`;
    case 'merge_cells':
      return `Merge ${c.sheet}!${c.range ?? ''}`;
    case 'unmerge_cells':
      return `Unmerge ${c.sheet}!${c.range ?? ''}`;
    case 'freeze_panes':
      return `Freeze panes on "${c.sheet}"`;
    case 'find_replace':
      return `Find/replace on ${c.sheet}${c.range ? `!${c.range}` : ''}`;
    case 'filter_range':
      return `Filter ${c.sheet}!${c.range ?? ''}`;
    case 'clear_filter':
      return `Clear filter on "${c.sheet}"`;
    case 'transpose_range':
      return `Transpose ${c.sheet}!${c.range ?? ''} → ${c.cell ?? ''}`;
    case 'text_to_columns':
      return `Split ${c.sheet}!${c.range ?? ''} into columns`;
    case 'create_named_range':
      return `Create named range "${c.named_range?.name ?? ''}"`;
    case 'delete_named_range':
      return `Delete named range "${c.named_range?.name ?? ''}"`;
    case 'apply_data_validation':
      return `Data validation (${c.validation?.type ?? ''}) on ${c.sheet}!${c.range ?? c.cell ?? ''}`;
    case 'clear_data_validation':
      return `Clear validation on ${c.sheet}!${c.range ?? c.cell ?? ''}`;
    case 'protect_sheet':
      return `Protect sheet "${c.sheet}"`;
    case 'unprotect_sheet':
      return `Unprotect sheet "${c.sheet}"`;
    case 'hide_sheet':
      return `Hide sheet "${c.sheet}"`;
    case 'show_sheet':
      return `Show sheet "${c.sheet}"`;
    case 'reorder_sheet':
      return `Move sheet "${c.sheet}" to position ${c.position ?? 0}`;
    case 'add_comment':
      return `Comment on ${c.sheet}!${c.cell ?? ''}`;
    case 'delete_comment':
      return `Delete comment on ${c.sheet}!${c.cell ?? ''}`;
    case 'apply_conditional_format':
      return `Conditional format (${c.conditional?.type ?? ''}) on ${c.sheet}!${c.range ?? c.cell ?? ''}`;
    case 'sort_range':
      return `Sort ${c.sheet}!${c.range ?? ''}`;
    case 'goal_seek':
      return `Goal Seek on ${c.sheet}!${c.goal_seek?.changing_cell ?? c.cell ?? ''}`;
    case 'sensitivity_table':
      return `Sensitivity table at ${c.sheet}!${c.sensitivity?.output_anchor ?? c.cell ?? ''}`;
    case 'scenarios':
      return `Scenario comparison at ${c.sheet}!${c.scenarios?.output_anchor ?? c.cell ?? ''}`;
    default:
      return c.sheet;
  }
}

// structuralDetails renders a compact second line with the spec payload.
function structuralDetails(c: ProposedChange): string {
  switch (c.type) {
    case 'create_chart': {
      const s = c.chart;
      if (!s) return '';
      const parts = [`source ${s.source_range}`];
      if (s.title) parts.push(`title "${s.title}"`);
      if (s.anchor_cell) parts.push(`anchor ${s.anchor_cell}`);
      return parts.join(' · ');
    }
    case 'create_pivot_table': {
      const s = c.pivot;
      if (!s) return '';
      const parts = [`source ${s.source_range}`, `→ ${s.destination_cell}`];
      if (s.rows?.length) parts.push(`rows: ${s.rows.join(', ')}`);
      if (s.columns?.length) parts.push(`cols: ${s.columns.join(', ')}`);
      if (s.values?.length) {
        parts.push(
          `values: ${s.values.map((v) => `${v.field}(${v.function})`).join(', ')}`,
        );
      }
      return parts.join(' · ');
    }
    case 'apply_conditional_format': {
      const s = c.conditional;
      if (!s) return '';
      const parts: string[] = [];
      if (s.operator) parts.push(`op ${s.operator}`);
      if (s.values?.length) parts.push(`values ${s.values.join(', ')}`);
      if (s.rank) parts.push(`rank ${s.rank}${s.bottom_rank ? ' (bottom)' : ''}${s.percent ? '%' : ''}`);
      if (s.min_color || s.max_color) {
        parts.push(`scale ${s.min_color ?? ''}→${s.max_color ?? ''}`);
      }
      if (s.bar_color) parts.push(`bar ${s.bar_color}`);
      return parts.join(' · ');
    }
    case 'sort_range': {
      const s = c.sort;
      if (!s) return '';
      const keyStr = s.keys
        .map((k) => `col ${k.key} ${k.ascending ? 'asc' : 'desc'}`)
        .join(', ');
      return s.has_header ? `${keyStr} (with header)` : keyStr;
    }
    case 'delete_rows_where': {
      const s = c.delete_rows_where;
      if (!s) return '';
      const parts = [`where ${s.column} ${s.op.replace(/_/g, ' ')}`];
      if (s.value !== undefined && s.value !== null && s.op !== 'empty') {
        parts.push(String(s.value));
      }
      if (s.op === 'between' && s.value2 !== undefined && s.value2 !== null) {
        parts.push(`and ${s.value2}`);
      }
      if (s.match_case) parts.push('(match case)');
      return parts.join(' ');
    }
    case 'goal_seek': {
      const s = c.goal_seek;
      if (!s) return '';
      return `set ${s.changing_cell} so that ${s.target_cell} = ${s.target_value}`;
    }
    case 'sensitivity_table': {
      const s = c.sensitivity;
      if (!s) return '';
      const isTwoVar = !!(s.col_input_cell && s.col_values && s.col_values.length > 0);
      if (isTwoVar) {
        return `2-var (${s.row_input_cell}: ${s.row_values.length} × ${s.col_input_cell}: ${s.col_values!.length}) of ${s.formula_cell}`;
      }
      return `1-var (${s.row_input_cell}: ${s.row_values.length} values) of ${s.formula_cell}`;
    }
    case 'scenarios': {
      const s = c.scenarios;
      if (!s) return '';
      const names = s.scenarios.map((sc) => sc.name).join(', ');
      return `${s.scenarios.length} scenarios [${names}] × ${s.output_cells.length} outputs`;
    }
    case 'resize_column':
      return c.resize?.autofit
        ? 'autofit to contents'
        : `width ${c.resize?.width ?? ''}`;
    case 'resize_row':
      return c.resize?.autofit
        ? 'autofit to contents'
        : `height ${c.resize?.height ?? ''}pt`;
    case 'freeze_panes': {
      const r = c.freeze?.rows ?? 0;
      const cc = c.freeze?.columns ?? 0;
      if (r === 0 && cc === 0) return 'unfreeze';
      return `${r > 0 ? `rows: ${r}` : ''}${r > 0 && cc > 0 ? ' · ' : ''}${cc > 0 ? `columns: ${cc}` : ''}`;
    }
    case 'find_replace': {
      const fr = c.find_replace;
      if (!fr) return '';
      const flags: string[] = [];
      if (fr.match_case) flags.push('match case');
      if (fr.whole_cell) flags.push('whole cell');
      const suffix = flags.length ? ` · ${flags.join(', ')}` : '';
      return `"${fr.find}" → "${fr.replace}"${suffix}`;
    }
    case 'filter_range': {
      const f = c.filter;
      if (!f) return '';
      const parts = [`col ${f.column}`, `kind ${f.kind}`];
      if (f.values?.length) parts.push(`values ${f.values.join(',')}`);
      if (f.rank) parts.push(`rank ${f.rank}${f.percent ? '%' : ''}`);
      return parts.join(' · ');
    }
    case 'text_to_columns': {
      const s = c.text_to_columns;
      if (!s) return '';
      if (s.delimiter) {
        const d = s.delimiter === '\t' ? 'tab' : `"${s.delimiter}"`;
        return `delimiter ${d}${s.treat_consecutive_as_one ? ' · collapse runs' : ''}`;
      }
      if (s.fixed_widths?.length) return `fixed widths ${s.fixed_widths.join(',')}`;
      return '';
    }
    case 'create_named_range': {
      const n = c.named_range;
      if (!n) return '';
      const scope = n.scope && n.scope.toLowerCase() !== 'workbook' ? ` (${n.scope})` : '';
      return `${n.refers ?? ''}${scope}`;
    }
    case 'delete_named_range': {
      const n = c.named_range;
      if (!n) return '';
      return n.scope && n.scope.toLowerCase() !== 'workbook' ? `scope: ${n.scope}` : 'workbook scope';
    }
    case 'apply_data_validation': {
      const v = c.validation;
      if (!v) return '';
      const parts: string[] = [];
      if (v.operator) parts.push(`op ${v.operator}`);
      if (v.values?.length) parts.push(`values ${v.values.join(', ')}`);
      if (v.formula) parts.push(`formula ${v.formula}`);
      return parts.join(' · ');
    }
    case 'protect_sheet': {
      const p = c.protect;
      if (!p) return '';
      const allows: string[] = [];
      if (p.allow_format_cells) allows.push('format');
      if (p.allow_sort) allows.push('sort');
      if (p.allow_filter) allows.push('filter');
      if (p.allow_insert_rows || p.allow_insert_cols) allows.push('insert');
      if (p.allow_delete_rows || p.allow_delete_cols) allows.push('delete');
      const prefix = p.password ? 'password-locked' : 'no password';
      return `${prefix}${allows.length ? ` · allow: ${allows.join(', ')}` : ''}`;
    }
    case 'add_comment':
      return c.comment?.text ? `"${c.comment.text.slice(0, 80)}${c.comment.text.length > 80 ? '…' : ''}"` : '';
    case 'unprotect_sheet':
      return c.protect?.password ? 'with password' : '';
    case 'merge_cells':
    case 'unmerge_cells':
    case 'clear_data_validation':
    case 'clear_filter':
    case 'delete_comment':
    case 'transpose_range':
    case 'text_to_columns':
    case 'filter_range':
    case 'hide_sheet':
    case 'show_sheet':
    case 'reorder_sheet':
    case 'delete_row':
    case 'delete_column':
    case 'refresh_pivot_table':
    case 'delete_chart':
    case 'delete_pivot_table':
      // These types either carry their full story in structuralLabel, or
      // were handled by a specific case above. Explicit listing makes the
      // exhaustiveness of the switch visible to future reviewers.
      return '';
    default:
      return '';
  }
}

function formatSide(formula?: string, value?: unknown): string {
  if (formula && formula.length > 0) return formula;
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function diffAfterSummary(c: ProposedChange): string {
  switch (c.type) {
    case 'set_range':
      return `${c.range} (range write)`;
    case 'format_cells':
      if (!c.format) return '(format)';
      return (
        'format: ' +
        Object.entries(c.format)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => `${k}=${v}`)
          .join(' · ')
      );
    case 'clear_cell':
      return '(clear)';
    case 'delete_range':
      return '(delete)';
    case 'insert_row':
      return '(insert row)';
    case 'insert_column':
      return '(insert column)';
    default:
      return '';
  }
}
